import { useFeatureFlagEnabled } from "@posthog/react";
import { useMemo } from "react";
import {
  useAvailableActivities,
  type AvailableActivity,
} from "@/lib/api/activities";

interface UseAvailableActivityItemsResult {
  items: AvailableActivity[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

// Backend availability with client-side feature-flag gating layered on top.
// Flag OFF -> keep the row but mark it "coming soon" (anticipation builder).
export function useAvailableActivityItems(
  mentorshipId: string,
): UseAvailableActivityItemsResult {
  const {
    data: availability,
    isLoading,
    isError,
    refetch,
  } = useAvailableActivities(mentorshipId);
  const hotTakesFlagEnabled = useFeatureFlagEnabled("activity_hot_takes") || import.meta.env.DEV;
  const deepThoughtsEnabled =
    useFeatureFlagEnabled("activity_deep_thoughts") || import.meta.env.DEV;

  const items = useMemo(() => {
    const list = availability?.activities ?? [];
    return list.map((a) => {
      const activity: AvailableActivity = { ...a };
      if (activity.type === "hot_takes") {
        activity.enabled = !!hotTakesFlagEnabled;
        activity.reason = hotTakesFlagEnabled ? null : "coming_soon";
      }
      if (activity.type === "deep_thoughts") {
        activity.enabled = !!deepThoughtsEnabled;
        activity.reason = deepThoughtsEnabled ? null : "coming_soon";
      }
      return activity;
    });
  }, [availability, hotTakesFlagEnabled, deepThoughtsEnabled]);

  return { items, isLoading, isError, refetch: () => void refetch() };
}

export function getActivityUnavailableTooltip(
  activity: AvailableActivity,
): string {
  if (!activity.enabled) {
    switch (activity.reason) {
      case "weekly_cap_reached":
        return "Nice momentum - you've hit this week's activity limit. Try again next week.";
      case "no_first_message":
        return "This unlocks once your teen sends their first message.";
      case "feature_flag_disabled":
      case "coming_soon":
        return "We're putting the finishing touches on this. Coming soon.";
      default:
        return "This activity isn't available right now. Check back soon.";
    }
  }

  if (!activity.isTeenEligible)
    return "This activity unlocks once your teen updates to the latest app version.";

  return "";
}
